import { axiosInstance } from "api/axios";

// Generyczny magazyn ustawień po stronie BE (klucz → JSON), zastępujący dane
// trzymane dotąd wyłącznie w localStorage. Scope:
//   "shared" — współdzielone w kancelarii (struktura org., słowniki, profil firmy…)
//   "user"   — prywatne użytkownika (preferencje, log prób e-mail…)
// Wartość przesyłamy jako string (surowy JSON) — BE traktuje ją nieprzezroczyście.

export type AppSettingScope = "shared" | "user";

export interface IAppSettingDto {
  key: string;
  valueJson: string | null;
  updatedAt: string | null;
}

// BE serializuje PascalCase (PropertyNamingPolicy = null).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const mapDto = (raw: any): IAppSettingDto => ({
  key: raw?.Key ?? raw?.key ?? "",
  valueJson: raw?.ValueJson ?? raw?.valueJson ?? null,
  updatedAt: raw?.UpdatedAt ?? raw?.updatedAt ?? null,
});

/** Odczyt pojedynczego ustawienia; zwraca surowy JSON lub null, gdy brak. */
export const getAppSetting = async (
  scope: AppSettingScope,
  key: string,
): Promise<string | null> => {
  const { data } = await axiosInstance.get(`/app-settings/${scope}/${encodeURIComponent(key)}`);
  return mapDto(data).valueJson;
};

/** Odczyt wielu kluczy jednym żądaniem → mapa { key: valueJson }. */
export const getAppSettingsBulk = async (
  scope: AppSettingScope,
  keys: string[],
): Promise<Record<string, string | null>> => {
  if (keys.length === 0) return {};
  const { data } = await axiosInstance.get(`/app-settings/${scope}`, {
    params: { keys: keys.join(",") },
  });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const items: any[] = data?.Items ?? data?.items ?? [];
  const out: Record<string, string | null> = {};
  for (const it of items) {
    const dto = mapDto(it);
    out[dto.key] = dto.valueJson;
  }
  return out;
};

/** Zapis (upsert) pojedynczego ustawienia. */
export const setAppSetting = async (
  scope: AppSettingScope,
  key: string,
  valueJson: string | null,
): Promise<void> => {
  await axiosInstance.put(`/app-settings/${scope}/${encodeURIComponent(key)}`, { valueJson });
};

/** Usunięcie ustawienia. */
export const deleteAppSetting = async (
  scope: AppSettingScope,
  key: string,
): Promise<void> => {
  await axiosInstance.delete(`/app-settings/${scope}/${encodeURIComponent(key)}`);
};
